export const PENDING_SEARCH_REDIRECT_KEY = 'disgenet_pending_search_redirect';

const ALLOWED_PATHS = new Set(['/search', '/examples']);

export type PendingSearchRedirect = {
  pathname: string;
  search: string;
};

type StoredPayload = PendingSearchRedirect & {
  calendarDay: string;
};

function getLocalCalendarDay(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function normalizeSearch(search: string): string {
  if (!search) {
    return '';
  }
  return search.startsWith('?') ? search : `?${search}`;
}

function isAllowedPath(pathname: string): boolean {
  return ALLOWED_PATHS.has(pathname);
}

export function savePendingSearchRedirect(pathname: string, search: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (!isAllowedPath(pathname)) {
    return;
  }
  const normalizedSearch = normalizeSearch(search);
  if (!normalizedSearch || normalizedSearch === '?') {
    return;
  }
  try {
    const payload: StoredPayload = {
      pathname,
      search: normalizedSearch,
      calendarDay: getLocalCalendarDay(),
    };
    window.localStorage.setItem(PENDING_SEARCH_REDIRECT_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

export function clearPendingSearchRedirect(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(PENDING_SEARCH_REDIRECT_KEY);
  } catch {
    /* ignore */
  }
}

function parseStored(raw: string | null): StoredPayload | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<StoredPayload>;
    if (
      typeof parsed.pathname !== 'string' ||
      typeof parsed.search !== 'string' ||
      typeof parsed.calendarDay !== 'string'
    ) {
      return null;
    }
    if (!isAllowedPath(parsed.pathname)) {
      return null;
    }
    return {
      pathname: parsed.pathname,
      search: normalizeSearch(parsed.search),
      calendarDay: parsed.calendarDay,
    };
  } catch {
    return null;
  }
}

export function readPendingSearchRedirect(): PendingSearchRedirect | null {
  if (typeof window === 'undefined') {
    return null;
  }
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(PENDING_SEARCH_REDIRECT_KEY);
  } catch {
    return null;
  }
  const parsed = parseStored(raw);
  if (!parsed || !parsed.search || parsed.search === '?') {
    clearPendingSearchRedirect();
    return null;
  }
  if (parsed.calendarDay !== getLocalCalendarDay()) {
    clearPendingSearchRedirect();
    return null;
  }
  return { pathname: parsed.pathname, search: parsed.search };
}

export function takePendingSearchRedirect(): PendingSearchRedirect | null {
  const pending = readPendingSearchRedirect();
  if (pending) {
    clearPendingSearchRedirect();
  }
  return pending;
}

export function consumePendingSearchRedirect(): PendingSearchRedirect | null {
  return takePendingSearchRedirect();
}

export type PostLoginRedirect = string | { pathname: string; search: string };

export function takePostLoginRedirectTarget(): PostLoginRedirect {
  const pending = takePendingSearchRedirect();
  if (!pending) {
    return '/';
  }
  return { pathname: pending.pathname, search: pending.search };
}
